// -----------------------------------------------------------------------------
// Time-of-day theme controller
// -----------------------------------------------------------------------------
// We support two saved palettes:
//   DAY   — pink / yellow / green (default)
//   NIGHT — pink / purple / blue
//
// Switching is automatic based on the user's local clock:
//   07:00 – 20:59  => day
//   21:00 – 06:59  => night
//
// The theme is applied by setting `data-theme` on <html>. All theme-aware
// styling lives in `index.css` under `:root[data-theme="day"]` and
// `:root[data-theme="night"]` selectors, and re-themable SVG icons read
// `--c-icon-1/2/3` CSS variables for their gradient stops.
//
// NOTE: we previously dynamically updated `<meta name="theme-color">` here
// (mint in day / blue in night) to tint the iOS Safari chrome to match the
// gradient bottom. The user disliked the visible solid colour at the top
// of mobile Safari (where the camera notch lives) — they want either the
// gradient itself bleeding through, or pure black. The static
// `theme-color="#000000"` in index.html combined with
// `apple-mobile-web-app-status-bar-style="black-translucent"` gives us:
//   • Mobile Safari URL bar: tinted dark (close to "black" the user
//     mentioned).
//   • PWA standalone: status bar is a translucent black overlay through
//     which the gradient pink top is partially visible — closest we can
//     get to "the gradient background" the user prefers.
// Therefore: NO dynamic theme-color update from this file.
// -----------------------------------------------------------------------------
import { useEffect } from 'react';

export const DAY_START_HOUR = 7;    // inclusive  → 07:00 (7 am)
export const NIGHT_START_HOUR = 21; // inclusive  → 21:00 (9 pm)

export function getThemeForDate(date = new Date()) {
  const h = date.getHours();
  return h >= DAY_START_HOUR && h < NIGHT_START_HOUR ? 'day' : 'night';
}

export function applyThemeNow() {
  if (typeof document === 'undefined') return 'day';
  const theme = getThemeForDate();
  if (document.documentElement.dataset.theme !== theme) {
    document.documentElement.dataset.theme = theme;
  }
  return theme;
}

// Hook: keeps document.documentElement.dataset.theme in sync with local time.
// Re-evaluates every 60 seconds, and also when the tab becomes visible again
// (so a phone that's been asleep for hours flips immediately on wake).
export function useTimeOfDayTheme() {
  useEffect(() => {
    applyThemeNow();
    const intervalId = setInterval(applyThemeNow, 60_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') applyThemeNow();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
